<template>
    <div class="applications-view">
        <div class="view-title-container">
            <div class="view-title-icon">
                <i class="fa-solid fa-file-pen"></i>
            </div>
            <div>
                <h2 class="view-title">Application Management</h2>
            </div>
        </div>

        <div class="view-body">
            <k-tabstrip :selected="selectedApplicationTab" @select="onAppTabSelected" :tabs="applicationTabs"
                class="app-management-tabstrip">
                <template v-slot:filteredapplications>
                    <div class="pipeline-container" v-if="!isLoading">
                        <div class="cc-apps-toolbar">
                            <div class="cc-apps-toolbar-left">
                                <div class="cc-apps-search-wrap">
                                    <i class="fa-duotone fa-magnifying-glass"></i>
                                    <input class="cc-apps-search" type="text" placeholder="Search in all columns..."
                                        :value="searchWord" @input="onSearchChange({ value: $event.target.value })" />
                                </div>

                                <div class="cc-apps-toggle" v-if="pipelineQuery?.isBranchManager">
                                    <span :class="['cc-apps-toggle-label', { 'cc-apps-toggle-label--on': showAllApplications }]">
                                        All Applications
                                    </span>
                                    <k-switch v-model="showAllApplications" :on-label="''" :off-label="''"></k-switch>
                                </div>
                            </div>

                            <div class="cc-apps-toolbar-right">
                                <button class="cc-apps-refresh-btn" @click="onRefreshApps">
                                    <i class="fa-regular fa-arrow-rotate-right"></i> Refresh
                                </button>
                            </div>
                        </div>

                        <CommandCenterTable
                            :show-header="false"
                            :columns="tabColumns"
                            :data="filteredItems"
                            :default-page-size="50"
                            :filterable="true"
                            :active-sort="sort"
                            @row-click="onRowClick"
                            @sort="onSortChange"
                            @filter-change="onColFilterChange">
                            <template #alertCell="{ dataItem }">
                                <span class="app-alert"
                                    v-if="activeApplicationAlerts?.some(a => a.applicationId === dataItem.applicationId)"
                                    title="Active Assistance Request">
                                    <i class="fa-solid fa-circle-exclamation"></i>
                                </span>
                            </template>

                            <template #pipelineActions="{ dataItem }">
                                <div class="pipeline-actions-container">
                                    <button v-if="selectedApplicationTab !== 4" class="cc-apps-delete-btn"
                                        @click.stop="onArchiveApp(dataItem)" title="Archive App">
                                        <i class="fa-duotone fa-trash"></i>
                                    </button>
                                    <button v-else class="cc-apps-restore-btn"
                                        @click.stop="restoreApp(dataItem.applicationId)" title="Restore App">
                                        <i class="fa-duotone fa-solid fa-arrow-rotate-left"></i>
                                    </button>
                                </div>
                            </template>
                            <template #analysisCell="{ dataItem }">
                                <button class="cc-ai-brain-btn" @click.stop="openAnalysis(dataItem)" title="AI Analysis">
                                    <i class="fa-duotone fa-microchip-ai"></i>
                                </button>
                            </template>
                        </CommandCenterTable>
                    </div>
                </template>

                <!-- ── Referrals Tab ── -->
                <template v-slot:referrals>
                    <div class="pipeline-container referrals-container">
                        <div class="cc-apps-toolbar">
                            <div class="cc-apps-toolbar-left">
                                <div class="cc-apps-search-wrap">
                                    <i class="fa-duotone fa-magnifying-glass"></i>
                                    <input class="cc-apps-search" type="text" placeholder="Search in all columns..."
                                        :value="referralSearchWord" @input="referralSearchWord = $event.target.value" />
                                </div>

                                <div class="referral-type-wrap">
                                    <label class="referral-type-label">Type</label>
                                    <select class="referral-type-select" v-model="referralType" @change="onReferralTypeChange">
                                        <option value="all">All</option>
                                        <option value="reverse">Reverse Mortgages</option>
                                        <option value="commercial">Commercial</option>
                                    </select>
                                </div>
                            </div>

                            <div class="cc-apps-toolbar-right">
                                <button class="cc-apps-refresh-btn" @click="onRefreshReferrals">
                                    <i class="fa-regular fa-arrow-rotate-right"></i> Refresh
                                </button>
                            </div>
                        </div>

                        <!-- Reverse only — borrower columns -->
                        <div v-if="referralType === 'reverse'" class="referrals-table-wrap">
                            <CommandCenterTable
                                :show-header="false"
                                :columns="reverseMortgageColumns"
                                :data="filteredReferralItems"
                                :default-page-size="50"
                                :filterable="true"
                                :active-sort="referralSort"
                                @sort="onReferralSortChange"
                                @row-click="onReferralRowClick">
                            </CommandCenterTable>
                        </div>

                        <!-- Commercial only — contact/entity columns -->
                        <div v-if="referralType === 'commercial'" class="referrals-table-wrap">
                            <CommandCenterTable
                                :show-header="false"
                                :columns="commercialLoanColumns"
                                :data="filteredReferralItems"
                                :default-page-size="50"
                                :filterable="true"
                                :active-sort="referralSort"
                                @sort="onReferralSortChange"
                                @row-click="onReferralRowClick">
                            </CommandCenterTable>
                        </div>

                        <!-- All — normalized shared columns -->
                        <div v-if="referralType === 'all'" class="referrals-table-wrap">
                            <CommandCenterTable
                                :show-header="false"
                                :columns="allReferralColumns"
                                :data="filteredReferralItems"
                                :default-page-size="50"
                                :filterable="true"
                                :active-sort="referralSort"
                                @sort="onReferralSortChange"
                                @row-click="onReferralRowClick">
                            </CommandCenterTable>
                        </div>
                    </div>
                </template>

            </k-tabstrip>

            <div class="alerts-container">
                <k-listview-header>
                    <div class="list-title">
                        <i class="fa-sharp-duotone fa-solid fa-bells"></i> Application Alerts
                        <div class="refresh-container">
                            <k-button @click="onRefreshAlerts" class="refresh-btn">
                                <i class="fa-regular fa-arrow-rotate-right"></i> Refresh
                            </k-button>
                        </div>
                    </div>
                    <div class="search-container">
                        <k-input class="alert-search-input" placeholder="Search by borrower name..."
                            v-model="alertSearchWord" :icon-name="'search'"></k-input>
                    </div>
                </k-listview-header>

                <k-listview :data-items="filteredActiveApplicationAlerts" item="alert" class="alerts-list"
                    v-if="filteredActiveApplicationAlerts?.length > 0">
                    <template v-slot:alert="{ props }">
                        <div class="alert-container">
                            <div class="date-actions">
                                <p class="alert-date">{{ new Date(props.dataItem.created).toLocaleDateString() }}</p>
                                <div class="alert-actions">
                                    <k-button class="alert-action"
                                        @click="() => onAlertClick({ dataItem: { applicationId: props.dataItem.applicationId, lar: props.dataItem.lar } })">
                                        <i class="fa-duotone fa-solid fa-arrow-up-right-from-square"></i>
                                        View app
                                    </k-button>
                                    <k-button class="alert-action" @click="() => clearApplicationAlert(props.dataItem)">
                                        <i class="fa-jelly fa-regular fa-check"></i>
                                        Clear alert
                                    </k-button>
                                </div>
                            </div>
                            <h4 class="full-name">{{ props.dataItem.firstName }} {{ props.dataItem.lastName }}</h4>
                            <div class="contact-container">
                                <div class="contact-item phone">
                                    <i class="fa-notdog-du fa-regular fa-phone" aria-label="Mobile Phone"></i>
                                    <p class="contact-text">
                                        <a v-if="props.dataItem.mobile" :href="'tel: ' + props.dataItem.mobile">
                                            {{ formatPhoneNumber(props.dataItem.mobile) }}
                                        </a>
                                        <span v-if="!props.dataItem.mobile">N/A</span>
                                    </p>
                                </div>
                                <div class="contact-item email">
                                    <i class="fa-regular fa-envelope" aria-label="Email"></i>
                                    <p class="contact-text">
                                        <a v-if="props.dataItem.email" :href="'mailto: ' + props.dataItem.email">
                                            {{ props.dataItem.email }}
                                        </a>
                                        <span v-if="!props.dataItem.email">N/A</span>
                                    </p>
                                </div>
                            </div>
                            <div class="description-container">
                                <p class="alert-description">{{ props.dataItem.description }}</p>
                            </div>
                        </div>
                    </template>
                </k-listview>

                <div class="no-alerts-container" v-if="filteredActiveApplicationAlerts?.length === 0 && !alertsLoading">
                    <p class="no-alerts"><i class="fa-duotone fa-thin fa-folder-open"></i> No Active Alerts Available</p>
                </div>
            </div>
        </div>

        <div class="component__loader-container" v-if="isLoading">
            <k-loader size="large" type="infinite-spinner"></k-loader>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>

        <confirm-dialog :showDialog="showConfirmDialog"
            :dialog-Msg="confirmMsg"
            @dialogclosed="toggleConfirmDialog"
            @cancelclicked="toggleConfirmDialog"
            @confirmclicked="archiveApp"></confirm-dialog>

        <k-notification-group class="default__notification-container">
            <k-fade :appear="successNotification">
                <k-notification v-if="successNotification"
                    :type="{ style: 'success', icon: true }"
                    :closable="true"
                    @close="onCloseNotification('successNotification')">
                    <span>{{ notificationMsg }}</span>
                </k-notification>
            </k-fade>
        </k-notification-group>
    </div>

    <teleport to="body">
        <ReverseMortgageDetailModal
            :open="referralDetailOpen"
            :item="selectedReferralItem || {}"
            @close="referralDetailOpen = false" />
    </teleport>

    <teleport to="body">
        <CommercialLoanDetailModal
            :open="commercialDetailOpen"
            :item="selectedCommercialItem || {}"
            @close="commercialDetailOpen = false" />
    </teleport>

    <PipelineAnalysisModal :open="analysisModalOpen" :loading="analysisLoading" :analysis="analysisData"
        @close="analysisModalOpen = false" />
</template>

<script>
    import PipelineAnalysisModal      from '@/components/PipelineAnalysisModal.vue'
    import ReverseMortgageDetailModal from '../components/ReverseMortgageDetailModal.vue'
    import CommercialLoanDetailModal  from '../components/CommercialLoanDetailModal.vue'
    import { TabStrip }               from '@progress/kendo-vue-layout';
    import { Loader }                 from "@progress/kendo-vue-indicators";
    import { Input, Switch }          from '@progress/kendo-vue-inputs';
    import { Button }                 from '@progress/kendo-vue-buttons';
    import { Dialog }                 from '@progress/kendo-vue-dialogs';
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade }                   from "@progress/kendo-vue-animation";
    import { ListView, ListViewHeader } from '@progress/kendo-vue-listview';
    import { orderBy, filterBy }      from '@progress/kendo-data-query';
    import confirmDialog              from '../components/confirmDialog.vue';
    import CommandCenterTable         from '../components/commandCenter/CommandCenterTable.vue';
    import cloneDeep                  from 'lodash.clonedeep';
    import apiService                 from '../api/api.service.js';
    import auth                       from '../authConfig.js';
    import formatHelpers              from '../helpers/formatHelpers.js';

    export default {
        name: 'Applications',

        data() {
            return {
                // ── Referral modal state ──────────────────────────────────
                selectedReferralItem:   null,
                referralDetailOpen:     false,
                selectedCommercialItem: null,
                commercialDetailOpen:   false,

                // ── Core app state ────────────────────────────────────────
                activeApplicationAlerts: [],
                alertsLoading:           true,
                alertSearchWord:         '',
                analysisModalOpen:       false,
                analysisLoading:         false,
                analysisData:            null,
                applicationsDaysBack:    "90",
                applicationTabs: [
                    { title: 'Incomplete', content: 'filteredapplications' },
                    { title: 'Submitted',  content: 'filteredapplications' },
                    { title: 'Referrals',  content: 'referrals'            },
                    { title: 'Drafts',     content: 'filteredapplications' },
                    { title: 'Archived',   content: 'filteredapplications' },
                ],
                archivedApp:            null,
                azureId:                null,
                confirmMsg:             '',
                errorMsg:               '',
                errorTitle:             '',
                colFilter:              null,
                filteredItems:          [],
                filteredSubmittedItems: [],
                incompleteApps:         [],
                isLoading:              false,
                notificationMsg:        '',
                pipelineQuery:          null,
                searchWord:             '',
                selectedApplicationTab: 0,
                showAllApplications:    false,
                showConfirmDialog:      false,
                showErrorDialog:        false,
                sort:                   [{ field: 'updated', dir: 'desc' }],
                submittedApps:          [],
                successNotification:    false,
                userApplications:       [],

                // ── Referrals state ───────────────────────────────────────
                referralType:         'all',
                referralSearchWord:   '',
                referralSort:         [{ field: 'submittedat', dir: 'desc' }],
                reverseMortgageItems: [],
                commercialLoanItems:  [],
            }
        },

        computed: {
            // Reverse — borrower-centric columns
            reverseMortgageColumns() {
                return [
                    { field: 'type',        title: 'Type'          },
                    { field: 'bwrfullname', title: 'Borrower'      },
                    { field: 'bwremail',    title: 'Email'          },
                    { field: 'bwrphone',    title: 'Phone'          },
                    { field: 'loname',      title: 'Loan Officer'  },
                    { field: 'submittedat', title: 'Submitted', type: 'datetime' },
                ];
            },

            // Commercial — contact/entity-centric columns
            commercialLoanColumns() {
                return [
                    { field: 'type',                title: 'Type'          },
                    { field: 'entityname',          title: 'Entity'        },
                    { field: 'contactname',         title: 'Contact'       },
                    { field: 'contactemail',        title: 'Email'         },
                    { field: 'contactphone',        title: 'Phone'         },
                    { field: 'propertytype',        title: 'Property Type' },
                    { field: 'loanamountrequested', title: 'Loan Amount'   },
                    { field: 'loanpurpose',         title: 'Purpose'       },
                    { field: 'loname',              title: 'Loan Officer'  },
                    { field: 'submittedat',         title: 'Submitted', type: 'datetime' },
                ];
            },

            // All — normalized columns that work for both types
            allReferralColumns() {
                return [
                    { field: 'type',         title: 'Type'              },
                    { field: 'displayname',  title: 'Borrower / Entity' },
                    { field: 'displayemail', title: 'Email'             },
                    { field: 'displayphone', title: 'Phone'             },
                    { field: 'loname',       title: 'Loan Officer'      },
                    { field: 'submittedat',  title: 'Submitted', type: 'datetime' },
                ];
            },

            filteredActiveApplicationAlerts() {
                if (!this.alertSearchWord) return this.activeApplicationAlerts;
                return this.activeApplicationAlerts.filter(alert =>
                    alert.fullName?.toLowerCase().includes(this.alertSearchWord.toLowerCase())
                );
            },

            filteredReferralItems() {
                let items = [];

                if (this.referralType === 'all' || this.referralType === 'reverse')
                    items = items.concat(cloneDeep(this.reverseMortgageItems));
                if (this.referralType === 'all' || this.referralType === 'commercial')
                    items = items.concat(cloneDeep(this.commercialLoanItems));

                if (this.referralSearchWord) {
                    const search = this.referralSearchWord.toLowerCase();
                    items = items.filter(item =>
                        Object.values(item).some(val => val && val.toString().toLowerCase().includes(search))
                    );
                }

                if (this.referralSort?.length) items = orderBy(items, this.referralSort);

                return items;
            },

            tabColumns() {
                const isBM = this.pipelineQuery?.isBranchManager || this.pipelineQuery?.isCorp;

                if (this.selectedApplicationTab === 1) {
                    const base = [
                        { cell: 'analysisCell', title: '', field: '__analysis', sortable: false, filterable: false },
                        { field: 'submittedBy',       title: 'Source'                                              },
                        { field: 'fullName',           title: isBM ? 'Borrower' : 'Name'                          },
                        { field: 'email',              title: 'Email'                                              },
                        { field: 'mobile',             title: 'Mobile'                                             },
                        { field: 'requestsCreated',   title: 'Doc Requests',  align: 'center', type: 'number', padding: '0px 12px 0px 0px' },
                        { field: 'requestsCompleted', title: 'Doc Uploads',   align: 'center', type: 'number', padding: '0px 12px 0px 0px' },
                        { field: 'voeStatus', title: 'VOE Status'},
                        { field: 'voaStatus', title: 'VOA Status'},
                        { field: 'submitted',          title: 'Submitted', type: 'datetime'                        },
                    ];
                    if (isBM) base.splice(4, 0, { field: 'loanOfficerName', title: 'Loan Officer' });
                    return base;
                }

                if (this.selectedApplicationTab === 3) { // Drafts
                    const base = [
                        { cell: 'alertCell', title: '', field: '__alert', sortable: false, filterable: false },
                        { field: 'fullName',             title: isBM ? 'Borrower' : 'Name' },
                        { field: 'created',              title: 'Started',        type: 'datetime' },
                        { field: 'lastCompletedSection', title: 'Last Completed'               },
                        { field: 'updated',              title: 'Modified',       type: 'datetime' },
                        { cell: 'pipelineActions', title: '', field: '__actions', sortable: false, filterable: false },
                    ];
                    if (isBM) base.splice(2, 0, { field: 'loanOfficerName', title: 'Loan Officer' });
                    return base;
                }

                if (this.selectedApplicationTab === 4) { // Archived
                    const base = [
                        { field: 'fullName',             title: isBM ? 'Borrower' : 'Name' },
                        { field: 'email',                title: 'Email'                     },
                        { field: 'mobile',               title: 'Mobile'                    },
                        { field: 'lastCompletedSection', title: 'Last Completed'            },
                        { field: 'updated',              title: 'Modified', type: 'datetime' },
                        { cell: 'pipelineActions', title: '', field: '__actions', sortable: false, filterable: false },
                    ];
                    if (isBM) base.splice(5, 0, { field: 'loanOfficerName', title: 'Loan Officer' });
                    return base;
                }

                // Incomplete (0) + fallback
                const base = [
                    { cell: 'alertCell', title: '', field: '__alert', sortable: false, filterable: false },
                    { field: 'fullName',             title: isBM ? 'Borrower' : 'Name' },
                    { field: 'email',                title: 'Email'                     },
                    { field: 'mobile',               title: 'Mobile'                    },
                    { field: 'created',              title: 'Started',       type: 'datetime' },
                    { field: 'lastCompletedSection', title: 'Last Completed'            },
                    { field: 'updated',              title: 'Modified',      type: 'datetime' },
                    { cell: 'pipelineActions', title: '', field: '__actions', sortable: false, filterable: false },
                ];
                if (isBM) base.splice(7, 0, { field: 'loanOfficerName', title: 'Loan Officer' });
                return base;
            },
        },

        watch: {
            '$route.query.tab': { handler() { this.applyTabFromQuery(); } },

            selectedApplicationTab(val) {
                this.isLoading = true;
                this.colFilter = null;

                if (val === 1) {
                    if (this.sort?.length === 1 && this.sort[0].field === 'updated')
                        this.sort = [{ field: 'submitted', dir: 'desc' }];
                } else {
                    if (this.sort?.length === 1 && this.sort[0].field === 'submitted')
                        this.sort = [{ field: 'updated', dir: 'desc' }];
                }

                if (val === 2) {
                    if (this.reverseMortgageItems.length === 0) this.loadReverseMortgageRequests();
                    if (this.commercialLoanItems.length === 0)  this.loadCommercialLoanRequests();
                }

                this.getData();
                this.isLoading = false;
            },

            showAllApplications() { this.getData(); },
        },

        async mounted() {
            this.applyTabFromQuery();
            const msalAppInstance = await auth.init();
            msalAppInstance.handleRedirectPromise().then(async tokenRes => {
                if (!tokenRes && !auth.user()) { auth.login(); }
                this.azureId = localStorage.getItem('azure_id');
                if (!this.azureId) return;
                this.isLoading = true;
                await this.getPipelineQuery();
                await this.getUserApplications();
                this.isLoading = false;
                this.getActiveApplicationAlertsByAzureId();
            });
        },

        methods: {
            // ── Referral row click ────────────────────────────────────────
            onReferralRowClick(row) {
                if (row.type === 'Commercial') {
                    this.selectedCommercialItem = row;
                    this.commercialDetailOpen   = true;
                } else {
                    this.selectedReferralItem = row;
                    this.referralDetailOpen   = true;
                }
            },

            applyTabFromQuery() {
                this.selectedApplicationTab = this.getTabIndexFromQuery(this.$route?.query?.tab);
            },

            async archiveApp() {
                this.toggleConfirmDialog();
                this.isLoading = true;
                const request = {
                    applicationId: this.archivedApp.applicationId,
                    azureId:       this.archivedApp.azureId,
                    archivedBy:    localStorage.getItem('user_name'),
                    pipelineQuery: this.pipelineQuery,
                };
                const tokenResponse = await auth.acquireTokenResponse();
                return apiService.ArchiveApplicationById(request, tokenResponse.accessToken)
                    .then(res => {
                        if (res?.data?.data) {
                            this.assignApps(res.data.data);
                            this.getActiveApplicationAlertsByAzureId();
                            this.isLoading = false;
                            this.showNotification('Application archived successfully');
                        }
                    }).catch(error => {
                        this.onRequestError('Failed to Archive Application',
                            error.response?.data?.data ? "Error: \n\n" + error.response.data.data : undefined);
                    });
            },

            assignApps(appData) {
                appData.forEach(app => {
                    if (app.application) app.application = JSON.parse(app.application);
                    app.created = formatHelpers.formatDateTime(app.created);
                    app.mobile  = formatHelpers.formatPhoneNumber(app.mobile);
                    if (app.loanId) {
                        if (app.application?.loanPurposeType?.value)
                            app.loanPurpose = app.application.loanPurposeType.display;
                        app.submitted = formatHelpers.formatDateTime(app.submitted);
                        app.voeStatus = app.voeStatus === 'Disabled' ? '—' : app.voeStatus;
                        app.voaStatus = app.voaStatus === 'Disabled' ? '—' : app.voaStatus;
                    } else {
                        app.updated = formatHelpers.formatDateTime(app.updated);
                    }
                });
                this.userApplications = appData;
                this.getData();
            },

            async clearApplicationAlert(alert) {
                alert.cleared = new Date();
                const request = { alert, azureId: this.azureId, pipelineQuery: this.pipelineQuery };
                this.isLoading = true;
                const tokenResponse = await auth.acquireTokenResponse();
                return apiService.ClearApplicationAlert(request, tokenResponse.accessToken)
                    .then(res => {
                        if (res?.data?.data) {
                            this.activeApplicationAlerts = res.data.data;
                            this.isLoading = false;
                            this.showNotification('Alert cleared successfully');
                        }
                    }).catch(error => {
                        this.onRequestError('Failed to Clear App Alert',
                            error.response?.data?.data ? "Error: \n\n" + error.response.data.data : undefined);
                    });
            },

            formatPhoneNumber(n) { return formatHelpers.formatPhoneNumber(n); },

            async getActiveApplicationAlertsByAzureId() {
                const tokenResponse = await auth.acquireTokenResponse();
                return apiService.GetActiveApplicationAlertsByAzureId(this.pipelineQuery, this.azureId, tokenResponse.accessToken)
                    .then(res => {
                        if (res?.data?.data) { this.activeApplicationAlerts = res.data.data; this.alertsLoading = false; }
                    }).catch(error => {
                        this.onRequestError('Failed to Fetch App Alerts',
                            error.response?.data?.data ? "Error: \n\n" + error.response.data.data : undefined);
                    });
            },

            getData() { this.filteredItems = this.getFilteredItems(); this.isLoading = false; },

            getFilteredItems() {
                let items = cloneDeep(this.userApplications);

                if (this.searchWord) {
                    const search = this.searchWord.toLowerCase();
                    items = items.filter(item => Object.values(item).some(val => val && val.toString().toLowerCase().includes(search)));
                }

                if (this.selectedApplicationTab !== 4) items = items.filter(i => !i.archived);
                if (this.selectedApplicationTab === 0) items = items.filter(i => i.auth0Id && !i.loanId);
                if (this.selectedApplicationTab === 1) items = items.filter(i => i.loanId);
                if (this.selectedApplicationTab === 3) items = items.filter(i => !i.auth0Id && !i.loanId);
                if (this.selectedApplicationTab === 4) items = items.filter(i => i.archived);

                if (this.pipelineQuery?.isBranchManager && !this.showAllApplications)
                    items = items.filter(i => i.azureId === this.azureId);

                if (this.colFilter)    items = filterBy(items, this.colFilter);
                if (this.sort?.length) items = orderBy(items, this.sort);

                return items;
            },

            async getPipelineQuery() {
                const tokenResponse = await auth.acquireTokenResponse();
                return apiService.GetSwiftAppPipelineQueryResponse(this.azureId, tokenResponse.accessToken)
                    .then(res => { if (res?.data?.data) this.pipelineQuery = res.data.data; })
                    .catch(error => {
                        this.onRequestError('Failed to Fetch Applications',
                            error.response?.data?.data ? "Error: \n\n" + error.response.data.data : undefined);
                    });
            },

            getTabIndexFromQuery(tabName) {
                if (!tabName) return 0;
                const idx = this.applicationTabs.findIndex(t => t.title?.toLowerCase() === tabName.toString().trim().toLowerCase());
                return idx >= 0 ? idx : 0;
            },

            async getUserApplications() {
                const tokenResponse = await auth.acquireTokenResponse();
                return apiService.GetApplicationsByAzureIdAndStartedDate(this.pipelineQuery, this.azureId, this.applicationsDaysBack, tokenResponse.accessToken)
                    .then(res => { if (res.data?.data) this.assignApps(res.data.data); })
                    .catch(error => {
                        this.onRequestError('Failed to Fetch Applications',
                            error.response?.data?.data ? "Error: \n\n" + error.response.data.data : undefined);
                    });
            },

            // ── Referral loaders ──────────────────────────────────────────
            async loadReverseMortgageRequests() {
                try {
                    this.isLoading = true;
                    const tokenResponse = await auth.acquireTokenResponse();
                    const res  = await apiService.GetReverseMortgageRequests(tokenResponse.accessToken, this.azureId);
                    const rows = res?.data?.data ?? res?.data ?? [];

                    this.reverseMortgageItems = rows.map(r => {
                        const norm = {};
                        Object.keys(r).forEach(k => { norm[k.toLowerCase()] = r[k]; });

                        if (norm.bwrphone)    norm.bwrphone    = formatHelpers.formatPhoneNumber(norm.bwrphone);
                        if (norm.submittedat) norm.submittedat = formatHelpers.formatDateTime(norm.submittedat);

                        // hardcoded type + normalized display fields for "all" view
                        norm.type         = 'Reverse';
                        norm.displayname  = norm.bwrfullname;
                        norm.displayemail = norm.bwremail;
                        norm.displayphone = norm.bwrphone;

                        return norm;
                    });
                } catch (err) {
                    this.onRequestError('Failed to Load Reverse Mortgage Requests', err?.message);
                } finally {
                    this.isLoading = false;
                }
            },

            async loadCommercialLoanRequests() {
                try {
                    const tokenResponse = await auth.acquireTokenResponse();
                    const res  = await apiService.GetCommercialLoanRequests(tokenResponse.accessToken, this.azureId);
                    const rows = res?.data?.data ?? res?.data ?? [];

                    this.commercialLoanItems = rows.map(r => {
                        const norm = {};
                        Object.keys(r).forEach(k => { norm[k.toLowerCase()] = r[k]; });

                        if (norm.contactphone) norm.contactphone = formatHelpers.formatPhoneNumber(norm.contactphone);
                        if (norm.submittedat)  norm.submittedat  = formatHelpers.formatDateTime(norm.submittedat);

                        // hardcoded type + normalized display fields for "all" view
                        norm.type         = 'Commercial';
                        norm.displayname  = norm.entityname || norm.contactname;
                        norm.displayemail = norm.contactemail;
                        norm.displayphone = norm.contactphone;

                        return norm;
                    });
                } catch (err) {
                    this.onRequestError('Failed to Load Commercial Loan Requests', err?.message);
                }
            },

            onReferralTypeChange() {
                if ((this.referralType === 'commercial' || this.referralType === 'all')
                    && this.commercialLoanItems.length === 0) {
                    this.loadCommercialLoanRequests();
                }
            },

            async onRefreshReferrals() {
                this.reverseMortgageItems = [];
                this.commercialLoanItems  = [];
                await Promise.all([
                    this.loadReverseMortgageRequests(),
                    this.loadCommercialLoanRequests(),
                ]);
                this.showNotification('Referrals refreshed successfully');
            },

            onReferralSortChange({ field, dir }) { this.referralSort = [{ field, dir }]; },

            // ── Standard app methods ──────────────────────────────────────
            onAlertClick(evt) {
                const item = evt?.dataItem ?? evt;
                window.open(`${process.env.VUE_APP_LOANLAUNCH_APP_ENDPOINT}/application?lar=${item.lar}&id=${item.applicationId}`, '_blank');
            },

            onAppTabSelected(evt) {
                this.selectedApplicationTab = evt.selected;
                this.updateTabQuery(evt.selected);
            },

            onArchiveApp(app) {
                this.archivedApp = app;
                this.confirmMsg  = 'Are you sure you would like to delete this app?';
                this.toggleConfirmDialog();
            },

            onCloseNotification(flag) { this[flag] = false; },

            onColFilterChange({ filters }) {
                if (!filters || filters.length === 0) {
                    this.colFilter = null;
                } else {
                    const dateFields = new Set(
                        this.tabColumns
                            .filter(c => c.type === 'date' || c.type === 'datetime')
                            .map(c => c.field)
                    );
                    const mapped = filters.map(f => {
                        if (dateFields.has(f.field)) {
                            // BOTH 'date' and 'datetime' columns must compare at DAY
                            // granularity. The filter control is <input type="date">, so
                            // there is no time-of-day to compare against. Cell values here
                            // are real Date objects (formatHelpers.formatDateTime returns a
                            // Date), so a raw 'eq' compared a full timestamp against local
                            // midnight and could never match — that is what made the
                            // Submitted filter return "No records found."
                            const d = this.parseAppDate(f.value);
                            if (!d || isNaN(d.getTime())) return null;

                            const op  = f.operator || 'eq';
                            const fMs = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

                            return {
                                field: f.field,
                                operator(cellRaw) {
                                    if (cellRaw == null || cellRaw === '') return false;
                                    const cd = cellRaw instanceof Date ? cellRaw : new Date(cellRaw);
                                    if (isNaN(cd.getTime())) return false;
                                    const cMs = new Date(cd.getFullYear(), cd.getMonth(), cd.getDate()).getTime();
                                    if (op === 'eq')  return cMs === fMs;
                                    if (op === 'gte') return cMs >= fMs;
                                    if (op === 'lte') return cMs <= fMs;
                                    if (op === 'gt')  return cMs >  fMs;
                                    if (op === 'lt')  return cMs <  fMs;
                                    return false;
                                }
                            };
                        }
                        return { field: f.field, operator: f.operator, value: f.value };
                    }).filter(Boolean);

                    this.colFilter = mapped.length ? { logic: 'and', filters: mapped } : null;
                }
                this.getData();
            },

            onRowClick(evt) {
                if (this.selectedApplicationTab === 4) return;
                const item = evt?.dataItem ?? evt;
                if (this.selectedApplicationTab === 1) {
                    window.open(`${process.env.VUE_APP_SWIFT_URL}LoanTasks/?id=${item.loanId}`);
                } else {
                    window.open(`${process.env.VUE_APP_LOANLAUNCH_APP_ENDPOINT}/application?lar=${item.lar}&id=${item.applicationId}`, '_blank');
                }
            },

            async onRefreshAlerts() {
                this.isLoading = true;
                await this.getActiveApplicationAlertsByAzureId();
                this.isLoading = false;
                this.showNotification('Application alerts refreshed successfully');
            },

            async onRefreshApps() {
                this.isLoading = true;
                await this.getUserApplications();
                this.showNotification('Applications refreshed successfully');
            },

            onRequestError(errorTitle, errorMsg) {
                this.isLoading     = false;
                this.alertsLoading = false;
                this.errorTitle    = errorTitle;
                this.errorMsg      = errorMsg || 'Please try again';
                this.showErrorDialog = true;
            },

            onSearchChange(evt) { this.searchWord = evt.value; this.getData(); },

            onSortChange({ field, dir }) { this.sort = [{ field, dir }]; this.getData(); },

            async openAnalysis(app) {
                this.analysisModalOpen = true;
                this.analysisLoading   = true;
                this.analysisData      = null;
                try {
                    const tokenResponse = await auth.acquireTokenResponse();
                    const res    = await apiService.GetLoanPipelineAnalysisByLoanId(app.loanId, tokenResponse.accessToken);
                    const record = res?.data?.data;
                    if (!record) return;
                    const raw    = record.analysisjson;
                    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    this.analysisData = parsed?.analysis?.data ?? parsed?.data ?? parsed ?? null;
                } catch {
                    this.analysisData = null;
                } finally {
                    this.analysisLoading = false;
                }
            },

            parseAppDate(raw) {
                if (!raw) return null;
                if (raw instanceof Date) return raw;
                const s = String(raw).trim();
                const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
                if (mdy) { const [, mo, dy, yr, hh = '0', mm = '0', ss = '0'] = mdy; return new Date(+yr, +mo - 1, +dy, +hh, +mm, +ss); }
                const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                if (isoDate) { const [, yr, mo, dy] = isoDate; return new Date(+yr, +mo - 1, +dy); }
                const isoLocal = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
                if (isoLocal) { const [, yr, mo, dy, hh, mm, ss = '0'] = isoLocal; return new Date(+yr, +mo - 1, +dy, +hh, +mm, +ss); }
                const d = new Date(s);
                return isNaN(d.getTime()) ? null : d;
            },

            async restoreApp(appId) {
                this.isLoading = true;
                const request  = { applicationId: appId, azureId: this.azureId, pipelineQuery: this.pipelineQuery };
                const tokenResponse = await auth.acquireTokenResponse();
                return apiService.RestoreApplicationById(request, tokenResponse.accessToken)
                    .then(res => {
                        if (res?.data?.data) {
                            this.assignApps(res.data.data);
                            this.getActiveApplicationAlertsByAzureId();
                            this.isLoading = false;
                            this.showNotification('Application restored successfully');
                        }
                    }).catch(error => {
                        this.onRequestError('Failed to Restore Application',
                            error.response?.data?.data ? "Error: \n\n" + error.response.data.data : undefined);
                    });
            },

            showNotification(msg) {
                this.notificationMsg     = msg;
                this.successNotification = true;
                setTimeout(() => { this.successNotification = false; }, 4000);
            },

            toggleConfirmDialog() { this.showConfirmDialog = !this.showConfirmDialog; },

            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;
                if (!this.showErrorDialog) this.errorTitle = '';
            },

            updateTabQuery(index) {
                const tabName = this.applicationTabs[index]?.title?.toLowerCase();
                if (!tabName) return;
                this.$router.replace({ query: { ...this.$route.query, tab: tabName } });
            },
        },

        components: {
            'k-loader':             Loader,
            'k-input':              Input,
            'k-dialog':             Dialog,
            'k-listview':           ListView,
            'k-listview-header':    ListViewHeader,
            'k-button':             Button,
            'k-notification':       Notification,
            'k-notification-group': NotificationGroup,
            'k-fade':               Fade,
            'confirm-dialog':       confirmDialog,
            'k-switch':             Switch,
            'k-tabstrip':           TabStrip,
            CommandCenterTable,
            PipelineAnalysisModal,
            ReverseMortgageDetailModal,
            CommercialLoanDetailModal,
        },
    }
</script>

<style>
    @import '/src/assets/css/applicationsView.css';

    /* ── Referrals tab extras ─────────────────────────────────────────── */
    .referral-type-wrap {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: 12px;
    }

    .referral-type-label {
        font-size: 13px;
        font-weight: 600;
        color: var(--text-muted, #64748b);
        white-space: nowrap;
    }

    .referral-type-select {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--border-color, #e2e8f0);
        border-radius: 6px;
        font-size: 13px;
        background: #fff;
        color: var(--text-primary, #0f172a);
        cursor: pointer;
        outline: none;
        transition: border-color 0.15s;
    }

    .referral-type-select:focus {
        border-color: var(--primary, #3b82f6);
    }

    /* Height chain for referrals tab so the table fills available space */
    .applications-view .pipeline-container.referrals-container {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .applications-view .pipeline-container.referrals-container .referrals-table-wrap {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
</style>